import React, { useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import AttachmentsList from "./AttachmentsList";
import { useDebounceFn } from "ahooks";
import { uploadFile } from "./helpers";
import { buildAccept } from "./acceptedTypes";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { useStore } from "@breward/store";

interface FileUploadProps {
  id?: string;
  label?: string | React.ReactNode;
  directory?: string;
  acceptedTypes?: string[];
  maxSize?: number;
  /** When true, `acceptedTypes` and `maxSize` are enforced on drop and on file selection. */
  enforceRestrictions?: boolean;
  value?: string | null;
  bordered?: boolean;
  placeholderIcon?: string;
  showAttachmentsList?: boolean;
  aspectRatio?: { w: number; h: number };
  height?: number;
  small?: boolean;
  previewVariant?: "default" | "avatar";
  onUpload: (files: any[]) => void;
  error?: string;
}

const FileUpload = ({
  id,
  label,
  acceptedTypes,
  value,
  maxSize,
  enforceRestrictions = false,
  onUpload,
  error,
  height = 48,
  directory = "",
  bordered = true,
  small = false,
  placeholderIcon = undefined,
  aspectRatio = { w: 3, h: 2 },
  previewVariant = "default",
  showAttachmentsList = false,
}: FileUploadProps) => {
  const authToken = useStore(state => state?.authToken)
  const [_value, setValue] = useState();
  const [t] = useTranslation();
  const [progress, setProgress] = useState(0);
  const [files, setFiles] = useState<any>([]);
  const IMAGE_TYPES = ["jpg", "jpeg", "png", "gif"];
  const isAvatarPreview = previewVariant === "avatar";

  const debounceProgress = useDebounceFn((progress) => setProgress(progress), {
    wait: 50,
  });

  const removeUpload = () => {
    setFiles([{ url: null }]);
    setValue(undefined);
    onUpload([{ url: null }]);
  };

  const handleFileOpen = () => {
    window.open(value, "__blank");
  };

  const getFileName = (fileUrl?: string) => {
    if (!fileUrl) return undefined;

    try {
      const pathname = new URL(fileUrl, window.location.origin).pathname;
      const fileName = pathname.split("/").pop();
      return fileName ? decodeURIComponent(fileName) : undefined;
    } catch {
      return fileUrl.split("?")[0].split("/").pop();
    }
  };

  const [rejection, setRejection] = useState<string | undefined>();

  const rejectionMessage = (code?: string) =>
    code === "file-too-large"
      ? t("global:shared.FileTooLarge", "This file is too large.")
      : t("global:shared.FileTypeNotAllowed", "This file type is not allowed. Accepted: {{types}}", {
          types: acceptedTypes?.map((type) => `.${type}`).join(", "),
        });

  const { getRootProps, getInputProps } = useDropzone({
    ...(enforceRestrictions
      ? { accept: buildAccept(acceptedTypes), maxSize }
      : {}),
    onDropRejected: (fileRejections) =>
      setRejection(rejectionMessage(fileRejections[0]?.errors?.[0]?.code)),
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) setRejection(undefined);
      const preparedFiles = acceptedFiles.map((file, index) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        })
      );

      preparedFiles.forEach((file) => {
        uploadFile(
          import.meta.env.VITE_API_HOST,
          file,
          directory,
          authToken,
          (progress) => debounceProgress.run(progress),
          (name, url) => {
            const uploadedFile = {
              file,
              name,
              url,
              preview: file.preview,
            };

            setFiles(files.concat(uploadedFile));
            onUpload([
              ...files,
              { file, name, url },
            ]);
          }
        );
      });
    },
  });

  const imageUpload = () => {
    return acceptedTypes?.filter((acceptedType) =>
      IMAGE_TYPES.includes(acceptedType)
    )?.length;
  };

  const imageUploaded = () => {
    return !!(
      imageUpload() &&
      (files?.[0]?.preview ||
        (files?.[0]?.path || value)?.match(/.*\.(png|jpg|jpeg)/) ||
        (files?.[0]?.path || value)?.match(/^http.*\/breward.*/))
    );
  };

  const fileTypeIcon = () => {
    const iconSize = small ? "h-5 w-5" : "h-12 w-12";
    if (placeholderIcon) {
      return (
        <div
          className={classNames(
            placeholderIcon,
            "text-content-200/70 dark:text-content-800/70 hover:text-content-200/70 dark:hover:text-content-800/70 transition-colors",
            iconSize
          )}
        />
      );
    } else {
      if (imageUpload()) {
        return (
          <div className={classNames(
            "icon-[ph--file-image] text-content-500 dark:text-content-400 hover:text-content-500 dark:hover:text-content-400 transition-colors",
            iconSize
          )} />
        );
      } else {
        return (
          <div className={classNames(
            "icon-[ph--file-plus] text-content-500 dark:text-content-400 hover:text-content-500 dark:hover:text-content-400 transition-colors",
            iconSize
          )} />
        );
      }
    }
  };

  const isUploading = progress > 0 && progress < 100;

  useEffect(() => {
    setValue(value as any);
    if (!value) {
      setFiles([]);
    }
  }, [value]);

  return (
    <div id={id} className="min-w-0 space-y-1.5 font-sans">
      {label && (
        <label className="font-sans mb-1.5 block text-sm font-medium text-primary-700 dark:text-primary-300">
          {label}
        </label>
      )}
      <div
        {...getRootProps()}
        className={classNames(
          "group relative flex min-w-0 cursor-pointer items-center justify-center transition-all",
          small ? "h-10 rounded-lg" : isAvatarPreview ? "rounded-2xl" : "rounded-lg",
          !(files?.[0]?.url || _value) && bordered &&
          "border-2 border-dashed border-primary-200 dark:border-primary-800 hover:border-primary-300 dark:hover:border-primary-700",
          (files?.[0]?.url || _value) &&
          "border border-solid border-primary-100 dark:border-primary-800/50 overflow-hidden",
          "hover:bg-primary-50 dark:hover:bg-primary-900/30",
          error && "border-error-300 dark:border-error-700"
        )}
        style={
          small
            ? undefined
            : isAvatarPreview
            ? { height: "144px", width: "144px" }
            : {
                aspectRatio: `${aspectRatio.w} / ${aspectRatio.h}`,
                width: "100%",
              }
        }
      >
        <input
          {...getInputProps()}
          className="sr-only"
          aria-label={`Upload ${label || 'file'}`}
        />

        {isUploading ? (
          <div className="flex items-center gap-2 text-primary-500 dark:text-primary-400">
            <div className="icon-[ph--spinner] h-4 w-4 animate-spin" />
            <span className="text-sm font-medium">{progress.toFixed(0)}%</span>
          </div>
        ) : (
          <>
            {files?.[0]?.url || _value ? (
              <div className="relative flex h-full w-full items-center justify-center">
                {imageUploaded() ? (
                  <>
                    <img
                      className={classNames(
                        "object-cover transition-transform group-hover:scale-105",
                        isAvatarPreview
                          ? "h-36 w-36 rounded-full border border-white/60 shadow-[0_8px_30px_-15px_rgba(15,23,42,0.8)] dark:border-primary-700/70"
                          : "h-full w-full"
                      )}
                      // Keep the local object URL while the parent propagates the
                      // uploaded storage path through `value`. The path is not a
                      // browser URL until it is normalized by the API on reload.
                      src={files[0]?.preview || value}
                      alt={label?.toString() || "Uploaded file"}
                      onError={(e) => {
                        const target = e.currentTarget;
                        target.onerror = null;
                        target.classList.add('hidden');
                        target.parentElement?.querySelector('.placeholder')?.classList.remove('hidden');
                      }}
                    />
                    <div
                      className="placeholder hidden absolute inset-0 items-center justify-center bg-primary-50 dark:bg-primary-900/30"
                    >
                      <div className="icon-[ph--image-square] h-8 w-8 text-primary-400 dark:text-primary-600" />
                    </div>
                    {!isAvatarPreview && (
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
                    )}
                    {isAvatarPreview && (
                      <div className="pointer-events-none absolute bottom-3 right-3 flex h-9 w-9 items-center justify-center rounded-full border border-white/40 bg-black/30 text-white backdrop-blur-md">
                        <div className="icon-[ph--camera-plus] h-5 w-5" />
                      </div>
                    )}
                  </>
                ) : (
                  <button
                    onClick={() => handleFileOpen()}
                    className="flex w-full min-w-0 items-center gap-2 rounded-md bg-primary-50 px-4 py-2 text-sm font-medium text-primary-600 transition-colors hover:bg-primary-100 dark:bg-primary-900/30 dark:text-primary-400 dark:hover:bg-primary-800/30"
                  >
                    <div className="icon-[ph--file] h-4 w-4" />
                    <span className="min-w-0 truncate">
                      {files[0]?.name ||
                        getFileName(value) ||
                        t("global:shared.ViewFile", "View file")}
                    </span>
                  </button>
                )}
                {!small && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeUpload();
                    }}
                    className={classNames(
                      "absolute rounded-full bg-black/20 p-1.5 text-white transition-opacity backdrop-blur-sm hover:bg-black/30 dark:bg-white/10 dark:hover:bg-white/20",
                      isAvatarPreview
                        ? "right-3 top-3 opacity-100 md:opacity-0 md:group-hover:opacity-100"
                        : "right-2 opacity-0 group-hover:opacity-100"
                    )}
                    aria-label="Remove file"
                  >
                    <div className="icon-[ph--x] h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ) : (
              <div className={classNames(
                "flex items-center justify-center w-full h-full gap-2",
                small ? "flex-row px-3 py-1.5" : "flex-col p-4",
                isAvatarPreview && "gap-3"
              )}>
                {fileTypeIcon()}
                {small ? (
                  <span className="text-xs font-medium text-primary-600 dark:text-primary-400">
                    {t("global:shared.UploadFileCompact", "Select file...")}
                  </span>
                ) : (
                  <div className="max-w-full text-center">
                    <div className="text-sm font-medium text-primary-600 dark:text-primary-400">
                      {isAvatarPreview
                        ? t("global:shared.UploadAvatar", "Upload avatar")
                        : imageUpload()
                        ? t("global:shared.DropImageHere", "Drop image here or click to upload")
                        : t("global:shared.DropFileHere", "Drop file here or click to upload")}
                    </div>
                    <div className="mt-1 text-xs text-primary-500">
                      {acceptedTypes?.map(type => `.${type}`).join(', ')}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
      {(error || rejection) && (
        <p role="alert" className="mt-1.5 text-sm font-medium text-error-600 dark:text-error-400">
          {error || rejection}
        </p>
      )}
      {showAttachmentsList && !small && <AttachmentsList attachments={files} />}
    </div>
  );
};

export default FileUpload;
